/* eslint-disable arrow-body-style */

import React, { useState } from 'react';
import classnames from 'classnames';
import { useForm, useField } from 'react-final-form-hooks';
import { Button, Form, FormProps, Modal, ModalProps } from 'tea-component';
import TagInput from '@gp/components/TagInput';
import { isEmptyList, formatTeaFormItemStatus, formatTeaFormItemMessage } from '@gp/utils/common';
import { validateEmail } from '@gp/utils/validators';
import { validateRecipients } from '../../utils';
import { EmailSendResultStatus } from '../../enums';
import { FIELD_COMMON_ERR_MSG } from '@gp/constants/const';
import { TAG_INPUT_PLACEHOLDER } from '../../constants';

import type { IBaseProps } from '@home/types/common';
import type { IRecord } from '@gp/types/global';
import type { IEmailVO, IEmailSendResult } from '../../types';

type IEmailTestVO = Pick<IEmailVO, 'recipients'>;

interface IEmailSenderTestProps extends IBaseProps {
  disabled?: boolean;
  isTestSending?: boolean;
  modalProps?: ModalProps;
  formProps?: FormProps;
  onTest?: (recipients: IEmailTestVO) => Promise<IEmailSendResult | undefined>;
  onCancel?: () => void;
}

const DEFAULT_FORM_MODEL: IEmailTestVO = {
  recipients: {
    tags: [],
    input: '',
  },
};

const handleValidate = (values?: IEmailTestVO): IRecord<string> => {
  if (!values) return { recipients: FIELD_COMMON_ERR_MSG };
  const errors: IRecord<string> = {};
  const entries = Object.entries(values);

  for (const entry of entries) {
    const [key, value] = entry;
    if (key === 'recipients') {
      validateRecipients(errors, value);
    }
  }

  return errors;
};

const getIsFormAvailable = (values?: IEmailTestVO): boolean => {
  const { recipients } = values || {};
  const { tags, input } = recipients || {};
  return !isEmptyList(tags) || !!input?.trim();
};

const EmailSenderTest: React.FC<IEmailSenderTestProps> = (props) => {
  const { style, className, disabled = false, isTestSending = false, modalProps, formProps, onTest, onCancel } = props;
  const { style: modalStyle, className: modalClassName, ...restModalProps } = modalProps || {};
  const { style: formStyle, className: formClassName, ...restFormProps } = formProps || {};

  const [visible, setVisible] = useState(false);

  const [submitted, setSubmitted] = useState(false);

  const handleConfirm = async (values: IEmailTestVO) => {
    try {
      const res = await onTest?.(values);
      if (res?.status === EmailSendResultStatus.Success) {
        handleClose();
      }
    } catch (exception) {}
  };

  const { form, values, handleSubmit } = useForm({
    initialValues: DEFAULT_FORM_MODEL,
    onSubmit: handleConfirm,
    validate: handleValidate,
  });

  const recipientsField = useField('recipients', form);

  const formAvailable = getIsFormAvailable(values);

  const handleSubmitInPage = () => {
    setSubmitted(true);
    handleSubmit?.();
  };

  const handleClose = () => {
    setSubmitted(false);
    setVisible(false);
    form?.restart();
  };

  return (
    <>
      <Button
        style={style}
        className={classnames('', className)}
        disabled={disabled}
        onClick={(e) => {
          e?.stopPropagation();
          setVisible(true);
        }}
      >
        Test
      </Button>
      <Modal
        {...restModalProps}
        style={modalStyle}
        className={classnames('dbg-common-email-sender__modal dbg-common-email-sender-test__modal', modalClassName)}
        caption="Send Test Email"
        size={680}
        visible={visible}
        disableCloseIcon={isTestSending}
        onClose={() => {
          handleClose();
        }}
      >
        <Modal.Body>
          <Form
            {...restFormProps}
            style={formStyle}
            className={classnames('dbg-common-email-sender__form dbg-common-email-sender-test__form', formClassName)}
            layout="fixed"
            fixedLabelWidth={80}
          >
            <Form.Item
              label="Recipients"
              required
              showStatusIcon={false}
              status={formatTeaFormItemStatus(submitted, recipientsField?.meta)}
              message={formatTeaFormItemMessage(submitted, recipientsField?.meta)}
            >
              <TagInput {...recipientsField?.input} placeholder={TAG_INPUT_PLACEHOLDER} validateTag={validateEmail} />
            </Form.Item>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="primary"
            loading={isTestSending}
            disabled={!formAvailable}
            onClick={(e) => {
              e?.stopPropagation();
              handleSubmitInPage();
            }}
          >
            Confirm
          </Button>
          <Button
            disabled={isTestSending}
            onClick={(e) => {
              e?.stopPropagation();
              handleClose();
              onCancel?.();
            }}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EmailSenderTest;
